import React, { useState, useEffect } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AuthDialog from "../AuthDialog";
import LoginPage from "../../../pages/AutenticationPages/LoginPage";
import RegisterPage from "../../../pages/AutenticationPages/RegisterPage";
import ForgotPasswordPage from "../../../pages/AutenticationPages/ForgotPasswordPage";
import VerificationPage from "../../../pages/AutenticationPages/VerificationPage";

const ResponsiveAuthWrapper = ({ initialView = "login" }) => {
  const [currentView, setCurrentView] = useState(initialView);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [isShowingMobilePage, setIsShowingMobilePage] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Use 600px breakpoint as requested
  const isDesktop = useMediaQuery('(min-width:600px)');

  // Handle responsive transitions
  useEffect(() => {
    // Update mobile page state based on screen size
    const shouldShowMobile = !isDesktop;
    setIsShowingMobilePage(shouldShowMobile);
  }, [isDesktop]);

  // Separate effect to handle navigation when transitioning from mobile to desktop
  useEffect(() => {
    // If we were showing mobile page and now we're desktop, navigate back
    if (isDesktop && isShowingMobilePage) {
      navigate("/");
    }
  }, [isDesktop, isShowingMobilePage, navigate]);

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleClose = () => {
    setDialogOpen(false);
    // Closing the login goes back to the page the user was on (e.g. they tapped Profile, saw
    // the login and closed it) instead of always dumping them on the home page. Opened
    // directly (email link, typed URL) there is no in-app page to go back to, so go home.
    if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const getComponent = () => {
    switch (currentView) {
      case "register":
        return (
          <RegisterPage 
            onClose={handleClose} 
            onViewChange={handleViewChange} 
            isMobile={!isDesktop}
          />
        );
      case "forgot":
        return (
          <ForgotPasswordPage
            onClose={handleClose}
            onViewChange={handleViewChange}
            isMobile={!isDesktop}
          />
        );
      case "verify":
        return (
          <VerificationPage
            onClose={handleClose}
            onViewChange={handleViewChange}
            isMobile={!isDesktop}
          />
        );
      case "login":
      default:
        return (
          <LoginPage 
            onClose={handleClose} 
            onViewChange={handleViewChange} 
            isMobile={!isDesktop} 
          />
        );
    }
  };

  // For desktop (600px and above) - show as popup dialog
  if (isDesktop) {
    return (
      <AuthDialog 
        open={dialogOpen} 
        onClose={handleClose} 
        initialView={initialView}
      />
    );
  }

  // For mobile (below 600px) - show as full page
  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: '#fff',
      zIndex: 9999,
      overflow: 'auto'
    }}>
      {getComponent()}
    </div>
  );
};

export default ResponsiveAuthWrapper;
